import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMonkeySpinPaused from '../../hooks/useMonkeySpinPaused';
import { formatJobsPerPerson } from '../../utils/monkeyRotation';

const MonkeyWorkloadCard = ({
  jobCount,
  rotationDuration,
  rotationSecondsLabel,
  workforce,
  loading = false
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [paused, setMonkeySpinPaused] = useMonkeySpinPaused();
  const perPerson = formatJobsPerPerson(
    jobCount / Math.max(1, workforce?.people || 1)
  );

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 sm:p-4 select-text cursor-default">
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-emerald-200 flex items-center justify-center shadow-sm">
          <span
            key={paused ? 'stopped' : `spin-${rotationDuration}`}
            className="text-xl sm:text-2xl font-bold text-green-800 inline-block"
            style={
              paused
                ? { transform: 'rotate(0deg)', animation: 'none' }
                : { animation: `spin-slow ${rotationDuration}s linear infinite` }
            }
          >
            @
          </span>
        </div>
        <p className="min-w-0 flex-1 text-sm sm:text-base font-semibold text-emerald-900 select-text leading-snug">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
              {t('dashboard.monkeyWorkload.loading', 'Učitavanje opterećenja...')}
            </span>
          ) : (
            t(
              'dashboard.monkeyWorkload.summary',
              'Manki @ - {{count}} otvorenih poslova - {{seconds}} s',
              { count: jobCount, seconds: rotationSecondsLabel }
            )
          )}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg bg-white border border-emerald-300 text-emerald-900 hover:bg-emerald-100 transition-colors"
          >
            {expanded
              ? t('dashboard.monkeyWorkload.seeLess', 'Vidi manje')
              : t('dashboard.monkeyWorkload.seeMore', 'Vidi više')}
          </button>
          <button
            type="button"
            onClick={() => setMonkeySpinPaused(!paused)}
            className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 transition-colors"
          >
            {paused
              ? t('dashboard.monkeyWorkload.start', 'Pokreni')
              : t('dashboard.monkeyWorkload.stop', 'Zaustavi')}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 sm:mt-4 sm:pt-4 border-t border-emerald-200 space-y-2 sm:space-y-3 text-sm text-emerald-900/90 leading-relaxed">
          <p>
            {t(
              'dashboard.monkeyWorkload.description',
              'Manki @ se okreće prema pravom opterećenju: ukupan broj otvorenih poslova (u kalendaru, nezakazanih i prosleđenih) deli se brojem radnika i partnera. 30 poslova po osobi = 1 krug u 30 sekundi, 60 poslova po osobi = 15 sekundi, 15 poslova po osobi = 60 sekundi.'
            )}
          </p>
          <p className="font-medium text-emerald-950">
            {t(
              'dashboard.monkeyWorkload.breakdown',
              '{{jobs}} otvorenih poslova / {{people}} ({{workers}} radnika + {{partners}} partnera) = {{perPerson}} po osobi',
              {
                jobs: jobCount,
                people: workforce?.people || 1,
                workers: workforce?.workers || 0,
                partners: workforce?.partners || 0,
                perPerson
              }
            )}
          </p>
          <p>
            {t(
              'dashboard.monkeyWorkload.motivation',
              'Ne dozvoli da se poslovi nakupe. Što je veće opterećenje po radniku i partneru, to se manki @ brže okreće. Završavaj poslove na vreme i drži ritam.'
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default MonkeyWorkloadCard;
